import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLidarClearance } from "../LidarClearanceContext";
import type { LidarClearanceSystem } from "../schemas";
import t from "../translations";

type Props = {
  open: boolean;
  region: string;
  systemId: number;
  onClose: () => void;
  onCopy: (source: LidarClearanceSystem) => void;
};

export default function CopyRulesConfigDialog({
  open,
  region,
  systemId,
  onClose,
  onCopy,
}: Props) {
  const { systems } = useLidarClearance();
  const [sourceSystemId, setSourceSystemId] = useState<number | "">("");

  useEffect(() => {
    if (open) setSourceSystemId("");
  }, [open]);

  // Any other system in the region with a saved config is a valid source -
  // an axis name the target doesn't already link is no longer auto-linked,
  // so a copied rule/action for that axis has no checkbox to edit (or, for
  // an entity check, only EntityCheckCard's raw-name fallback) until the
  // admin links it manually. Copying works best between systems that
  // already share the same linked axes.
  const sourceOptions = useMemo(
    () =>
      systems
        .filter(
          (system) =>
            system.region === region && system.id !== systemId && system.config
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [systems, region, systemId]
  );

  const handleCopy = () => {
    const source = sourceOptions.find((system) => system.id === sourceSystemId);
    if (!source) return;
    onCopy(source);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{t.CopyRulesConfigDialogTitle}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <FormControl fullWidth size="small" disabled={sourceOptions.length === 0}>
          <InputLabel id="copy-rules-config-source-label">
            {t.CopyRulesFromSystemLabel}
          </InputLabel>
          <Select
            labelId="copy-rules-config-source-label"
            label={t.CopyRulesFromSystemLabel}
            value={sourceSystemId}
            onChange={(event) => setSourceSystemId(Number(event.target.value))}
          >
            {sourceOptions.map((system) => (
              <MenuItem key={system.id} value={system.id}>
                {system.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {sourceOptions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t.NoOtherSystemsWithConfig}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t.CancelButton}</Button>
        <Button onClick={handleCopy} variant="contained" disabled={sourceSystemId === ""}>
          {t.CopyButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
